// Shopify

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (!elementToFocus) return;
  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

//
// Shopify: Components
//

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  isOpen() {
    return this.mainDetailsToggle.open;
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden');
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class MobileMenuDrawer extends MenuDrawer {
  constructor() {
    super();

    this.header = this.header || this.closest('powerful-header');
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);

    this.header.activate();
    document.body.classList.add('overflow-hidden');
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);

    this.header.deactivate();
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-height',
        `${parseInt(this.header.getBoundingClientRect().height)}px`
      );
  };
}

customElements.define('mobile-menu-drawer', MobileMenuDrawer);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
    );

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-option')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
    });
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(
          `Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');
        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        const addButtonPrice = addButtonUpdated.querySelector('.price');
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.variantStrings.soldOut,
          true,
          addButtonPrice
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true, price) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const addButtonPrice = addButton.querySelector('.price');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (price && addButtonPrice) {
      addButtonPrice.innerHTML = price.innerHTML;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

//
// Alina
//

class Intersector {
  constructor(options = {}) {
    return new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.add('intersector');

        if (entry.isIntersecting) {
          entry.target.classList.add('intersected');
          CustomIntersectorObserver.unobserve(entry.target);
        }
      });
    }, options);
  }
}

let CustomIntersectorObserver = new Intersector();

class CustomIntersector extends HTMLElement {
  constructor() {
    super();
    this.selector = this.getAttribute('selector');
  }

  connectedCallback() {
    this.reset();
  }

  reset() {
    CustomIntersectorObserver.observe(this);
    this.classList.remove('intersected');

    if (this.selector) {
      this.querySelectorAll(this.selector).forEach((el) => {
        CustomIntersectorObserver.observe(el);
        this.classList.remove('intersected');
      });
    }
  }
}

customElements.define('custom-intersector', CustomIntersector);

class DetailsToggle extends HTMLElement {
  connectedCallback() {
    this.cta = this.querySelector("[href^='#']");
    this.detailsId = this.cta.href.split('#')[1];
    this.detailsElement = document.getElementById(this.detailsId);

    if (!this.detailsElement) return;

    this.summaryElement = this.detailsElement.querySelector('summary');
    this.singleMode = this.dataset.mode == 'single';
    this.others = [...this.parentElement.getElementsByTagName(this.nodeName)].filter((e) => e != this);

    this.setLinkState();
    this.addEventListener('click', this.onClick);
    this.summaryElement?.addEventListener('click', this.onClick.bind(this));
  }

  onClick(ev) {
    ev.preventDefault(ev);
    this.toggle();
  }

  toggle(open = !this.detailsElement.open || this.singleMode) {
    this.detailsElement.toggleAttribute('open', open);
    this.setLinkState();

    if (!open || !this.singleMode) return;
    this.closeOthers();
  }

  close() {
    this.toggle(false);
    this.resetCustomIntersectors();
  }

  closeOthers() {
    this.others.forEach((toggle) => {
      toggle.close();
    });
  }

  setLinkState() {
    this.cta.classList.toggle('active', this.detailsElement.open);
  }

  resetCustomIntersectors() {
    this.detailsElement.querySelectorAll('custom-intersector').forEach((el) => {
      el.reset();
    });
  }
}

customElements.define('details-toggle', DetailsToggle);

const MoodGalleryItemsPerSlide = 2;

class MoodGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.mood-gallery__item'));
    this.slidesCount = Math.ceil(this.items.length / MoodGalleryItemsPerSlide);
    this.currentSlideIndex = 0;

    this.toggleSlide(0);
    this.observeVisibility();
  }

  observeVisibility() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.start();
          } else {
            this.stop();
          }
        });
      },
      {
        threshold: 0.25,
      }
    );
    this.intersectionObserver.observe(this);
  }

  nextSlide() {
    this.items[this.currentSlideIndex * MoodGalleryItemsPerSlide + 0]?.classList.add('shown');
    this.items[this.currentSlideIndex * MoodGalleryItemsPerSlide + 1]?.classList.add('shown');

    this.toggleSlide(this.currentSlideIndex);

    this.currentSlideIndex++;
    if (this.currentSlideIndex == this.slidesCount) {
      this.currentSlideIndex = 0;
    }

    this.toggleSlide(this.currentSlideIndex);
  }

  toggleSlide(index) {
    this.items[index * MoodGalleryItemsPerSlide + 0]?.classList.toggle('active');
    this.items[index * MoodGalleryItemsPerSlide + 1]?.classList.toggle('active');
  }

  start() {
    clearInterval(this.interval);
    this.interval = setInterval(this.nextSlide.bind(this), window.durations.long);
  }

  stop() {
    clearInterval(this.interval);
  }

  enableAutoplay() {
    this.autoplay = true;
  }
}

customElements.define('mood-gallery', MoodGallery);

class MoodBoard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelectorAll('.mood-board__item').forEach((block) => CustomIntersectorObserver.observe(block));
  }
}

customElements.define('mood-board', MoodBoard);

class MoodLists extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('nav a');
    this.panels = this.querySelectorAll('.mood-lists__panel');
    this.panelLinks = this.querySelectorAll('.mood-lists__panel a');

    CustomIntersectorObserver.observe(this);
    this.observeTabs();
    this.observePanelsAndLinks();
  }

  observeTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.tabClicked.bind(this));
    });
  }

  observePanelsAndLinks() {
    this.panels.forEach((panel) => {
      panel.addEventListener('mouseleave', this.panelUnfocused.bind(this));
    });
    this.panelLinks.forEach((link) => {
      link.addEventListener('focus', this.panelLinkFocused.bind(this));
      link.addEventListener('mouseenter', this.panelLinkFocused.bind(this));
    });
  }

  panelUnfocused(ev) {
    ev.preventDefault();

    this.panelLinks.forEach((panelLink) => {
      panelLink.classList.remove('background');
      panelLink.classList.remove('active');
    });
  }

  panelLinkFocused(ev) {
    ev.preventDefault();

    this.activatePanelLink(ev.target);
  }

  activatePanelLink(activePanelLink) {
    this.panelLinks.forEach((panelLink) => {
      panelLink.classList.toggle('background', activePanelLink != panelLink);
      panelLink.classList.toggle('active', activePanelLink == panelLink);
    });
  }

  tabClicked(ev) {
    ev.preventDefault();

    this.activateTab(ev.target);
  }

  activateTab(tab) {
    const lastActiveTab = this.querySelector('[aria-selected=true]');

    this.toggleTab(lastActiveTab, false);
    this.toggleTab(tab, true);
  }

  toggleTab(tab, show) {
    if (!tab) return;

    const panel = document.getElementById(tab.getAttribute('aria-controls'));
    tab.ariaSelected = show;
    panel.classList.toggle('visible', show);
    panel.hidden = !show;
  }
}

customElements.define('mood-lists', MoodLists);

class MediaSlider extends HTMLElement {
  constructor() {
    super();

    this.mediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onMediaIntersecting(entry.target);
          }
        });
      },
      {
        threshold: 0.5,
      }
    );
    this.images = [...this.querySelectorAll('.media')];
    this.images.forEach((image) => this.mediaObserver.observe(image));
  }

  onMediaIntersecting(media) {
    this.dataset.slide = this.images.indexOf(media) + 1;
  }
}

customElements.define('media-slider', MediaSlider);

class StackedMedia extends HTMLElement {
  constructor() {
    super();

    this.images = this.querySelectorAll('.stacked-media');

    this.total = this.images.length;
    this.page = 1;

    this.addEventListener('click', this.onClick);
  }

  onClick(ev) {
    ev.preventDefault();
    this.nextPage();
  }

  nextPage() {
    this.page++;
    if (this.page > this.total) this.page = 1;

    this.activatePage(this.page);
  }

  activatePage(page) {
    this.images.forEach((image) => image.classList.remove('active'));
    this.images[page - 1].classList.add('active');
    this.dataset.page = page;
  }
}

customElements.define('stacked-media', StackedMedia);

class PowerfulSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.grid = this.querySelector('.grid');
    this.items = this.querySelectorAll('.slider__item');
    this.prevLink = this.querySelector('.slider-pager-previous');
    this.nextLink = this.querySelector('.slider-pager-next');

    this.columns = window.breakpoints.mobile.matches ? 3 : 4;
    this.rootMargin = this.dataset.rootMargin || '0px -70% 0px 0px';

    this.observeItems();
    this.observePagerLinks();
  }

  observeItems() {
    this.intersectionObserver = new Intersector({
      root: this.grid,
      rootMargin: '0px 100% 0px 0px',
    });
    // Observe all items for reaching left part of the slider
    this.currentItemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activateItem(entry.target);
          }
        });
      },
      {
        rootMargin: this.rootMargin,
        threshold: 0.5,
      }
    );
    // Observe last item for intersection
    this.lastItemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.nextLink.toggleAttribute('aria-disabled', entry.isIntersecting);
        });
      },
      {
        threshold: 0.5,
      }
    );

    this.items.forEach((item, index) => {
      const card = item.querySelector('product-card, dialog-modal');
      this.intersectionObserver.observe(card);

      this.currentItemObserver.observe(item);
      if (index == this.items.length - 1) {
        this.lastItemObserver.observe([...this.items].at(-1));
      }
    });
  }

  observePagerLinks() {
    this.prevLink?.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.scrollToPreviousItem();
    });
    this.nextLink?.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.scrollToNextItem();
    });
  }

  scrollToPreviousItem() {
    this.scrollToItem(this.currentItem.previousElementSibling);
  }

  scrollToNextItem() {
    this.scrollToItem(this.currentItem.nextElementSibling);
  }

  scrollToItem(item) {
    this.grid.scroll({
      left: item.offsetLeft,
      behavior: 'smooth',
    });

    // This cannot be used right now, because Firefox does not support the inline option
    // item?.scrollIntoView({
    //   behavior: 'smooth',
    //   block: 'nearest',
    //   inline: 'start',
    // });
  }

  activateItem(item) {
    this.currentItem = item;

    if (this.prevLink) {
      this.prevLink.toggleAttribute('aria-disabled', !this.currentItem.previousElementSibling);
    }
  }
}

customElements.define('powerful-slider', PowerfulSlider);

class RunningHeadline extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    CustomIntersectorObserver.observe(this);

    this.wrapper = document.createElement('span');
    this.wrapper.append(this.textContent);
    this.textContent = '';
    this.append(this.wrapper);

    this.innerWidth = this.getBoundingClientRect().width;
    this.wrapperWidth = this.wrapper.getBoundingClientRect().width;
    this.cloneTimes = Math.ceil(this.innerWidth / this.wrapperWidth);

    Array(this.cloneTimes)
      .fill(0)
      .forEach((i, index) => {
        this.append(this.wrapper.cloneNode(true));
      });

    this.style.setProperty('--running-headline-duration', (1 / this.innerWidth) * this.wrapperWidth);
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.start();
        } else {
          this.stop();
        }
      });
    });
    this.observer.observe(this);
  }

  start() {
    this.classList.add('intersected');
  }

  stop() {
    this.classList.remove('intersected');
  }
}

customElements.define('running-headline', RunningHeadline);

class CombinedProductPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.links = [...this.querySelectorAll('a')];

    this.addEventListener('focus', this.updateCurrentValue);
    this.addEventListener('mouseover', this.updateCurrentValue);
    this.addEventListener('mouseout', this.resetCurrentValue);
  }

  updateCurrentValueElement(element, reset = false) {
    let currentValueElement = element.closest('[data-current-value]');
    let activeValueElement = currentValueElement.querySelector('.active');
    let text = (reset ? activeValueElement : element).textContent;

    currentValueElement.dataset.currentValue = text;
  }

  updateCurrentValue(ev) {
    if (!this.links.includes(ev.target)) return;

    this.updateCurrentValueElement(ev.target);
  }

  resetCurrentValue(ev) {
    if (!this.links.includes(ev.target)) return;

    this.updateCurrentValueElement(ev.target, true);
  }
}

customElements.define('product-series-picker', CombinedProductPicker);
